import './Gallery.css';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

// Dynamically import all gallery images
const galleryImages = import.meta.glob('../../Data/images/gallary/*.{jpg,jpeg,png,gif}', { eager: true });
const BASE_IMAGES = Object.values(galleryImages).map((module: any) => module.default);

export default function Gallery() {
  const [isActive, setIsActive] = useState(false);
  const [images, setImages] = useState<string[]>(BASE_IMAGES);
  const galleryRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const goTo = () => {
    navigate('/gallery');
  };

  // Initialize images on component mount
  useEffect(() => {
    setImages(BASE_IMAGES);

    // Intersection Observer for fade-in animation
    const observer = new IntersectionObserver(
      ([entry]) => setIsActive(entry.isIntersecting),
      { threshold: 0.1 }
    );

    if (galleryRef.current) observer.observe(galleryRef.current);
    return () => {
      if (galleryRef.current) observer.unobserve(galleryRef.current);
    };
  }, []);

  // Split images into 3 columns
  const firstColumnImages = images.slice(0, Math.ceil(images.length / 3));
  const secondColumnImages = images.slice(Math.ceil(images.length / 3), 2 * Math.ceil(images.length / 3));
  const thirdColumnImages = images.slice(2 * Math.ceil(images.length / 3));

  // Duplicate for infinite scroll
  const duplicatedFirstColumn = [...firstColumnImages, ...firstColumnImages];
  const duplicatedSecondColumn = [...secondColumnImages, ...secondColumnImages];
  const duplicatedThirdColumn = [...thirdColumnImages, ...thirdColumnImages];

  return (
    <div
      onClick={goTo}
      ref={galleryRef}
      className={clsx(
        "p-4 sm:p-8 lg:p-24 w-full shad lg:h-[700px] md:h-[600px] sm:h-[600px] items-center flex-1 flex justify-around flex-col lg:flex-row overflow-hidden gap-3 sm:gap-4",
        { 'opacity-0 translate-y-8': !isActive },
        { 'opacity-100 translate-y-0 transition-all duration-1000 delay-200 ease-in-out': isActive }
      )}
    >
      {/* First Column - Scroll Up */}
      <div className={clsx(
        "hidden lg:flex flex-col scroll-container lg:w-[300px] md:w-[500px] sm:w-[250px] items-center justify-start gap-3",
        { 'opacity-0 translate-y-8': !isActive },
        { 'opacity-100 translate-y-0 transition-all duration-700 delay-300 ease-in-out': isActive }
      )}>
        {images.length > 0 && (
          <div className="scroll-track-up">
            {duplicatedFirstColumn.map((imageUrl, index) => (
              <img key={index} src={imageUrl} alt="Gallery" className="rounded-3xl mb-3 flex-shrink-0" />
            ))}
          </div>
        )}
      </div>

      {/* Second Column - Scroll Down */}
      <div className={clsx(
        "flex flex-col scroll-container w-full max-w-[320px] sm:max-w-[420px] md:w-[500px] lg:w-[400px] items-center justify-start gap-3 mx-0 lg:mx-2",
        { 'opacity-0 translate-y-8': !isActive },
        { 'opacity-100 translate-y-0 transition-all duration-1000 delay-700 ease-in-out': isActive }
      )}>
        {images.length > 0 && (
          <div className="scroll-track-down">
            {duplicatedSecondColumn.map((imageUrl, index) => (
              <img key={index} src={imageUrl} alt="Gallery" className="rounded-3xl mb-3 flex-shrink-0" />
            ))}
          </div>
        )}
      </div>

      {/* Third Column - Scroll Up */}
      <div className={clsx(
        "hidden lg:flex flex-col scroll-container lg:w-[300px] md:w-[500px] sm:w-[250px] items-center justify-start gap-3",
        { 'opacity-0 translate-y-8': !isActive },
        { 'opacity-100 translate-y-0 transition-all duration-700 delay-500 ease-in-out': isActive }
      )}>
        {images.length > 0 && (
          <div className="scroll-track-up">
            {duplicatedThirdColumn.map((imageUrl, index) => (
              <img key={index} src={imageUrl} alt="Gallery" className="rounded-3xl mb-3 flex-shrink-0" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
